import { Fragment, useEffect, useMemo, useState } from 'react';

import styled from 'styled-components';

import { Rotate } from '@components/animators/Rotate';
import { Button } from '@components/button';
import { DesignAvatar } from '@components/design-avatar';
import { EmptySearchState } from '@components/empty-search-state';
import { ExpandableCard } from '@components/expandable-card';
import { FocusBox } from '@components/focus-box';
import { IconButton } from '@components/icon-button';
import { IconAddPlus } from '@components/icons/add-plus';
import { IconCloseRegular } from '@components/icons/close-regular';
import { IconDropRight } from '@components/icons/dropright';
import { IconSidemenuRightOpen } from '@components/icons/sidemenu-right-open';
import { IconStatus } from '@components/icons/status';
import { IconVisibilityShown } from '@components/icons/visibility-shown';
import { InlineSidebar } from '@components/inline-sidebar/inline-sidebar';
import { SearchBox } from '@components/search-box';
import { SquareImage } from '@components/square-image';
import { Horizontal, Stack } from '@components/stack';
import { Switch } from '@components/switch';
import { SmallColumnIcon } from '@components/table/column-icon';
import { ColumnManager } from '@components/table/fields-sidebar';
import { TableStore } from '@components/table/table-store';
import { AvailableColumn, ColumnType } from '@components/table/types';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';
import { pxToRem } from '@utils/converters';

import {
  ACTION_TYPE,
  ActionsDataAction,
  ActionsDataField,
  TravellerJourneyListItem,
} from 'api/types';
import { ActionIcon, getActionLabel } from 'ui/actions/action-config';
import { nodeTypes } from 'ui/journey-builder/node-types';

import { makeColumnId, makeVariableColumnId } from './traveller-journey-list/columns';
import { excludedActionTypes } from './traveller-journey-list/constants';
import { ExtraStateStore } from './traveller-journey-list/extra-state-store';
import { useTravellerJourneyListTranslate } from './traveller-journey-list/locale';
import { TravellersColumnControlStore } from './traveller-journey-list/travellers-column-control-store';

export const CustomColumnControl = ({
  actionsData,
  availableColumns,
  tableStore,
  extraStateStore,
  columnControlStore,
}: {
  actionsData: Array<ActionsDataAction>;
  tableStore: TableStore;
  availableColumns: Array<AvailableColumn<TravellerJourneyListItem>>;
  extraStateStore: ExtraStateStore;
  columnControlStore: TravellersColumnControlStore;
}) => {
  const { t } = useScopedTranslate(locale);
  const { useColumnOrder, useColumnVisibility } = tableStore;
  const { columnVisibility, setColumnVisibility } = useColumnVisibility();
  const { columnOrder, setColumnOrder } = useColumnOrder();
  const { actions, useIsOpen } = columnControlStore;

  const availableIds = useMemo(
    () => availableColumns.map((column) => column.id),
    [availableColumns]
  );

  const isOpen = useIsOpen();

  const allowedActions = useMemo(() => {
    return actionsData.filter((action) => {
      return !excludedActionTypes.includes(action.action);
    });
  }, [actionsData]);

  const columnsWithCustomNamesAndClickHandlers = useMemo(() => {
    return availableColumns.map((column) => {
      return {
        ...column,
        title: column.title,
        ...(column.id.startsWith('action__') && {
          extraContent: (
            <IconButton
              size="sm"
              variant="ghost-purple"
              icon={<IconSidemenuRightOpen size={16} />}
              onClick={() => {
                actions.focusColumn(column.id);
              }}
            />
          ),
        }),
      };
    });
  }, [availableColumns, actions]);

  return (
    <>
      <Button
        size="sm"
        variant="secondary"
        iconLeft={<IconVisibilityShown />}
        text={t('columns')}
        onClick={() => actions.setOpenness(!isOpen)}
      />

      <InlineSidebar
        overlay
        isOpen={isOpen}
        onOpenChange={actions.setOpenness}
        ariaLabel={t('ariaLabel')}
      >
        <InlineSidebar.ExpandedPanel isExpanded={isOpen}>
          <ActionsList
            actions={allowedActions}
            actionsData={actionsData}
            extraStateStore={extraStateStore}
            columnControlStore={columnControlStore}
            availableIds={availableIds}
            onColumnToggle={actions.toggleColumn}
            onClose={actions.close}
          />
        </InlineSidebar.ExpandedPanel>
        <InlineSidebar.MainPanel>
          <InlineSidebar.Header>
            <Horizontal space={2} align="center" justify="space-between">
              <Horizontal space={2}>
                <IconVisibilityShown color="elementBlue100" />
                <Text font="bold18" text={t('showHide')} />
              </Horizontal>
              <IconButton
                icon={<IconCloseRegular />}
                variant="ghost-purple"
                onClick={actions.close}
                dataTestid="close-sidebar"
              />
            </Horizontal>
          </InlineSidebar.Header>

          <InlineSidebar.Content>
            <ColumnManager
              availableColumns={columnsWithCustomNamesAndClickHandlers}
              columnOrder={columnOrder}
              columnVisibility={columnVisibility}
              onOrderUpdate={setColumnOrder}
              onVisibilityUpdate={setColumnVisibility}
            />
          </InlineSidebar.Content>
        </InlineSidebar.MainPanel>
      </InlineSidebar>
    </>
  );
};

const ActionsList = ({
  actions,
  actionsData,
  extraStateStore,
  columnControlStore,
  availableIds,
  onColumnToggle,
  onClose,
}: {
  actions: Array<ActionsDataAction>;
  actionsData: Array<ActionsDataAction>;
  extraStateStore: ExtraStateStore;
  columnControlStore: TravellersColumnControlStore;
  availableIds: Array<string>;
  onColumnToggle(id: string, value: boolean): void;
  onClose(): void;
}) => {
  const { t } = useScopedTranslate(locale);
  const { useAddedActionIds, useCustomNames } = extraStateStore;
  const addedActionIds = useAddedActionIds();
  const customNames = useCustomNames();
  const focusedVariableId = columnControlStore.useFocusedVariableId();
  const expandedActionIds = columnControlStore.useExpandedActionIds();

  const [search, setSearch] = useState('');

  const possibleResults = usePossibleResults(actions, customNames);

  const trimmedSearch = search.trim().toLowerCase();

  const { visibleActions, matchingFieldIdsByAction } = useMemo(() => {
    const allowed = actions.filter((action) => action.action !== 'instantiate_form');
    if (!trimmedSearch) {
      return { visibleActions: allowed, matchingFieldIdsByAction: null };
    }
    const map = new Map<string, Set<string>>();
    for (const result of possibleResults) {
      if (!result.search.includes(trimmedSearch)) continue;
      let set = map.get(result.actionShortId);
      if (!set) {
        set = new Set();
        map.set(result.actionShortId, set);
      }
      set.add(result.id);
    }
    return {
      visibleActions: allowed.filter((action) => map.has(action.shortId)),
      matchingFieldIdsByAction: map,
    };
  }, [actions, possibleResults, trimmedSearch]);

  const isSearching = trimmedSearch.length > 0;

  const [collapsedDuringSearch, setCollapsedDuringSearch] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!isSearching) {
      setCollapsedDuringSearch(new Set());
    }
  }, [isSearching]);

  const isCardOpen = (actionId: string) =>
    isSearching ? !collapsedDuringSearch.has(actionId) : expandedActionIds.has(actionId);

  const toggleCard = (actionId: string) => {
    if (isSearching) {
      setCollapsedDuringSearch((prev) => {
        const next = new Set(prev);
        if (next.has(actionId)) {
          next.delete(actionId);
        } else {
          next.add(actionId);
        }
        return next;
      });
    } else {
      columnControlStore.actions.toggleExpanded(actionId);
    }
  };

  const isAllCollapsed = isSearching
    ? visibleActions.length > 0 &&
      visibleActions.every((action) => collapsedDuringSearch.has(action.shortId))
    : expandedActionIds.size === 0;

  const toggleAllExpanded = () => {
    if (isSearching) {
      if (isAllCollapsed) {
        setCollapsedDuringSearch(new Set());
      } else {
        setCollapsedDuringSearch(new Set(visibleActions.map((action) => action.shortId)));
      }
    } else if (isAllCollapsed) {
      columnControlStore.actions.setExpanded(
        new Set(visibleActions.map((action) => action.shortId))
      );
    } else {
      columnControlStore.actions.setExpanded(new Set());
    }
  };

  return (
    <Fragment>
      <InlineSidebar.Header>
        <Horizontal space={2} align="center" justify="space-between">
          <Horizontal space={2} align="center">
            <IconAddPlus color="elementBlue100" />
            <Text font="bold18" text={t('addFromJourneyAction')} />
          </Horizontal>
          <IconButton
            icon={<IconCloseRegular />}
            variant="ghost-purple"
            onClick={onClose}
            dataTestid="close-add-field-sidebar"
          />
        </Horizontal>
      </InlineSidebar.Header>

      <InlineSidebar.Content>
        <Stack space={4}>
          <SearchRow space={2} align="center">
            <SearchBox placeholder={t('core.search')} defaultValue={search} onChange={setSearch} />
            <Button
              variant="secondary"
              text={t(isAllCollapsed ? 'expandAll' : 'collapseAll')}
              onClick={toggleAllExpanded}
              iconRight={
                <Rotate isRotated={!isAllCollapsed}>
                  <IconDropRight />
                </Rotate>
              }
              style={{ minWidth: pxToRem(144) }}
            />
          </SearchRow>
          {isSearching && visibleActions.length === 0 ? (
            <EmptySearchState variant="small" skipMargin />
          ) : (
            <Stack space={4}>
              <Text font="semibold14" text={t('actionsInJourney')} />
              <Stack space={2}>
                {visibleActions.map((action) => {
                  const visibleFieldIds = matchingFieldIdsByAction?.get(action.shortId);
                  return (
                    <ActionExpandableCard
                      key={action.shortId}
                      action={action}
                      actionData={actionsData.find((current) => current.shortId === action.shortId)}
                      addedActionIds={addedActionIds}
                      availableIds={availableIds}
                      customNames={customNames}
                      focus={focusedVariableId}
                      visibleFieldIds={visibleFieldIds}
                      isOpen={isCardOpen(action.shortId)}
                      onToggle={() => toggleCard(action.shortId)}
                      onColumnToggle={onColumnToggle}
                    />
                  );
                })}
              </Stack>
            </Stack>
          )}
        </Stack>
      </InlineSidebar.Content>
    </Fragment>
  );
};

const ActionExpandableCard = ({
  action,
  actionData,
  addedActionIds,
  availableIds,
  customNames,
  focus,
  visibleFieldIds,
  isOpen,
  onToggle,
  onColumnToggle,
}: {
  action: ActionsDataAction;
  actionData?: ActionsDataAction;
  addedActionIds: Array<string>;
  availableIds: Array<string>;
  customNames: Record<string, string>;
  focus?: string;
  visibleFieldIds?: Set<string>;
  isOpen: boolean;
  onToggle(): void;
  onColumnToggle(id: string, value: boolean): void;
}) => {
  const { t } = useScopedTranslate(locale);
  const { t: listT } = useTravellerJourneyListTranslate();

  if (action.action === 'instantiate_form') {
    return null;
  }

  const title = action.name ?? getActionLabel(action.action);
  const descriptionFn = nodeTypes[action.action]?.getDescription;
  const description = action.description || descriptionFn?.(action);
  const icon = action.avatar ? (
    <DesignAvatar src={action.avatar} />
  ) : action.logo ? (
    <SquareImage src={action.logo} />
  ) : (
    <ActionIcon action={action.action} />
  );

  const addedCount = availableIds.filter(
    (id) => id.startsWith(makeColumnId(action.shortId)) && addedActionIds.includes(id)
  ).length;

  const isFieldVisible = (id: string) => !visibleFieldIds || visibleFieldIds.has(id);

  const statusId = makeColumnId(action.shortId, 'status');
  const dateCompletedId = makeColumnId(action.shortId, 'date_completed');
  const statusLabel = formatFieldLabel(customNames[statusId], listT('action_status'));
  const dateCompletedLabel = formatFieldLabel(
    customNames[dateCompletedId],
    listT('action_date_completed')
  );

  const hasStatus = isFieldVisible(statusId);
  const hasDateCompleted = isFieldVisible(dateCompletedId);

  return (
    <ExpandableCard
      title={title}
      icon={icon}
      description={description || t('noDescription')}
      counter={addedCount > 0 ? addedCount : undefined}
      isOpen={isOpen}
      onToggle={onToggle}
    >
      <Stack space={3}>
        {(hasStatus || hasDateCompleted) && (
          <FixedFieldsGroup>
            {hasStatus && (
              <FieldFocusBox isFocused={focus === statusId}>
                <Switch
                  size="sm"
                  iconLeft={<IconStatus size={16} />}
                  label={statusLabel}
                  value={addedActionIds.includes(statusId)}
                  onChange={(value) => onColumnToggle(statusId, value)}
                />
              </FieldFocusBox>
            )}
            {hasDateCompleted && (
              <FieldFocusBox isFocused={focus === dateCompletedId}>
                <Switch
                  size="sm"
                  iconLeft={<SmallColumnIcon type="date" />}
                  label={dateCompletedLabel}
                  value={addedActionIds.includes(dateCompletedId)}
                  onChange={(value) => onColumnToggle(dateCompletedId, value)}
                />
              </FieldFocusBox>
            )}
          </FixedFieldsGroup>
        )}
        {actionData && (
          <ActionVariables
            focus={focus}
            data={actionData}
            action={action}
            addedActionIds={addedActionIds}
            customNames={customNames}
            onColumnToggle={onColumnToggle}
            visibleFieldIds={visibleFieldIds}
          />
        )}
      </Stack>
    </ExpandableCard>
  );
};

const VariableSwitches = ({
  fields: variables,
  focusedId,
  action,
  onColumnToggle,
  addedActionIds,
  customNames,
  visibleFieldIds,
  canToggleAll = false,
}: {
  fields: Array<ActionsDataField>;
  action: ActionsDataAction;
  focusedId?: string;
  onColumnToggle(id: string, value: boolean): void;
  addedActionIds: Array<string>;
  customNames: Record<string, string>;
  visibleFieldIds?: Set<string>;
  canToggleAll?: boolean;
}) => {
  const { t } = useScopedTranslate(locale);

  const visibleVariables = visibleFieldIds
    ? variables.filter((variable) =>
        visibleFieldIds.has(makeVariableColumnId(action.shortId, variable.id))
      )
    : variables;

  if (visibleVariables.length === 0) {
    return null;
  }

  const allIds = visibleVariables.map((variable) =>
    makeVariableColumnId(action.shortId, variable.id)
  );
  const isAllSelected = allIds.every((id) => addedActionIds.includes(id));

  const toggleAll = (value: boolean) => {
    allIds.forEach((id) => onColumnToggle(id, value));
  };

  return (
    <div>
      {canToggleAll && !visibleFieldIds && allIds.length > 1 && (
        <FieldRow>
          <Switch
            size="sm"
            label={t('showAllVariables')}
            value={isAllSelected}
            onChange={toggleAll}
          />
        </FieldRow>
      )}
      {visibleVariables.map((variable) => {
        const id = makeVariableColumnId(action.shortId, variable.id);
        const label = formatFieldLabel(customNames[id], variable.label);
        return (
          <FieldFocusBox isFocused={focusedId === id} key={id}>
            <Switch
              size="sm"
              iconLeft={<SmallColumnIcon type={variable.type} />}
              label={label}
              value={addedActionIds.includes(id)}
              onChange={(value) => onColumnToggle(id, value)}
            />
          </FieldFocusBox>
        );
      })}
    </div>
  );
};

const ActionVariables = ({
  action,
  data,
  onColumnToggle,
  focus,
  addedActionIds,
  customNames,
  visibleFieldIds,
}: {
  action: ActionsDataAction;
  data: ActionsDataAction;
  focus?: string;
  onColumnToggle(id: string, value: boolean): void;
  addedActionIds: Array<string>;
  customNames: Record<string, string>;
  visibleFieldIds?: Set<string>;
}) => {
  const { t } = useScopedTranslate(locale);

  const hasVisible = (fields: Array<ActionsDataField>) => {
    if (!visibleFieldIds) return fields.length > 0;
    return fields.some((field) =>
      visibleFieldIds.has(makeVariableColumnId(action.shortId, field.id))
    );
  };

  switch (action.action) {
    case 'combo_send_form': {
      const formResponses = data.variables.formResponses;
      if (!formResponses || !hasVisible(formResponses.fields)) {
        return null;
      }

      return (
        <Stack space={2}>
          <Text font="bold14" text={t('formResponses')} color="textSecondary" caps />
          <VariableSwitches
            canToggleAll
            action={action}
            addedActionIds={addedActionIds}
            customNames={customNames}
            focusedId={focus}
            onColumnToggle={onColumnToggle}
            fields={formResponses.fields}
            visibleFieldIds={visibleFieldIds}
          />
        </Stack>
      );
    }
    case 'create_document': {
      const documentFields = data.variables.documentFields;
      const actionFields = data.variables.actionOutput?.fields ?? [];
      if (!documentFields) {
        return null;
      }
      const showDocumentFields = hasVisible(documentFields.fields);
      const showActionOutput = actionFields.length > 0 && hasVisible(actionFields);

      if (!showDocumentFields && !showActionOutput) {
        return null;
      }

      return (
        <Stack space={3}>
          {showDocumentFields && (
            <Stack space={2}>
              <Text font="bold14" text={t('documentFields')} color="textSecondary" caps />
              <VariableSwitches
                canToggleAll
                action={action}
                focusedId={focus}
                addedActionIds={addedActionIds}
                customNames={customNames}
                onColumnToggle={onColumnToggle}
                fields={documentFields.fields}
                visibleFieldIds={visibleFieldIds}
              />
            </Stack>
          )}

          {showActionOutput && (
            <Stack space={2}>
              <Text font="bold14" text={t('actionOutput')} color="textSecondary" caps />
              <VariableSwitches
                action={action}
                focusedId={focus}
                addedActionIds={addedActionIds}
                customNames={customNames}
                onColumnToggle={onColumnToggle}
                fields={actionFields}
                visibleFieldIds={visibleFieldIds}
              />
            </Stack>
          )}
        </Stack>
      );
    }
    case 'send_sms':
    case 'send_email':
    case 'ask_ai':
    case 'wait_for_time':
    case 'wait_until_fixed_date':
    case 'wait_until_relative_date':
    case 'convert_document':
    case 'integration':
    case 'condition':
    case 'condition_v2':
    default: {
      const actionFields = data.variables.actionOutput?.fields ?? [];
      const configFields = data.variables.configuration?.fields ?? [];
      const hasActionOutput = actionFields.length > 0;
      const fields = hasActionOutput ? actionFields : configFields;

      if (fields.length === 0 || !hasVisible(fields)) {
        return null;
      }

      return (
        <Stack space={2}>
          <Text
            font="bold14"
            text={hasActionOutput ? t('actionOutput') : t('configuration')}
            color="textSecondary"
            caps
          />
          <VariableSwitches
            canToggleAll={['integration', 'ask_ai_agent'].includes(action.action)}
            action={action}
            focusedId={focus}
            addedActionIds={addedActionIds}
            customNames={customNames}
            onColumnToggle={onColumnToggle}
            fields={fields}
            visibleFieldIds={visibleFieldIds}
          />
        </Stack>
      );
    }
  }
};

const usePossibleResults = (
  actions: Array<ActionsDataAction>,
  customNames: Record<string, string>
) => {
  const { t } = useTravellerJourneyListTranslate();
  return useMemo(() => {
    const results: Array<PreparedSearchResult> = [];

    actions.forEach((action) => {
      const actionSearchTerms = [
        getActionLabel(action.action),
        action.name ?? '',
        action.description ?? '',
      ];

      (['status', 'date_completed'] as const).forEach((fixedProperty) => {
        const id = makeColumnId(action.shortId, fixedProperty);
        const label = formatFieldLabel(customNames[id], t(`action_${fixedProperty}`));

        results.push({
          actionShortId: action.shortId,
          action: action.action,
          id,
          label,
          search: [label, ...actionSearchTerms].join(' ').toLowerCase(),
          columnType: fixedProperty === 'status' ? 'single_select' : 'date',
        });
      });

      Object.values(action.variables ?? {}).forEach((variable) => {
        variable.fields.forEach((field) => {
          const id = makeVariableColumnId(action.shortId, field.id);
          const customName = customNames[id];
          const label = formatFieldLabel(customName, field.label);

          const search: Array<string> = [
            label,
            variable.name ?? '',
            variable.description ?? '',
            customName,
            ...actionSearchTerms,
          ];

          results.push({
            actionShortId: action.shortId,
            action: action.action,
            id,
            label,
            columnType: field.type,
            search: search.join(' ').toLowerCase(),
          });
        });
      });
    });

    return results;
  }, [actions, customNames, t]);
};

type PreparedSearchResult = {
  actionShortId: string;
  action: ACTION_TYPE;
  id: string;
  label: string;
  search: string;
  columnType: ColumnType;
};

const formatFieldLabel = (customName: string | undefined, fallback: string) =>
  customName ? `${customName} (${fallback})` : fallback;

const SearchRow = styled(Horizontal)({
  '.search-box-container': {
    flexGrow: 1,
  },
});

const FieldRow = styled.div({
  padding: pxToRem(4),
});

const FieldFocusBox = styled(FocusBox)({
  padding: pxToRem(4),
});

const FixedFieldsGroup = styled.div({
  display: 'flex',
  flexDirection: 'column',
});

const locale = {
  en: {
    columns: 'Fields',
    noDescription: 'No description',
    addFromJourneyAction: 'Add field from Journey action',
    actionsInJourney: 'Actions in journey',
    actionOutput: 'Action output',
    configuration: 'Configuration',
    documentFields: 'Document fields',
    formResponses: 'Form responses - dynamic',
    showHide: 'Show / Hide fields',
    showAllVariables: 'Show/Hide all',
    expandAll: 'Expand all',
    collapseAll: 'Collapse all',
    ariaLabel: 'Manage columns',
  },
};
